import { useEffect, useState, useCallback } from 'react'
import { getChannelInstanceQr, getChannelInstanceStatus } from '@/services/channelsApi'

interface QrModalProps {
  instanceId: string
  instanceName: string
  onClose: () => void
  onSuccess?: () => void
}

export default function ChannelQrModal({ instanceId, instanceName, onClose, onSuccess }: QrModalProps) {
  const [qrData, setQrData] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [connected, setConnected] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchQR = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const result = await getChannelInstanceQr(instanceId)
      if (result.qr) {
        setQrData(result.qr)
      } else {
        setError('QR no disponible aún. La instancia puede estar inicializándose, intenta en unos segundos.')
      }
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Error al obtener el código QR')
    } finally {
      setLoading(false)
    }
  }, [instanceId])

  const checkStatus = useCallback(async () => {
    try {
      const result = await getChannelInstanceStatus(instanceId)
      if (result.status === 'CONNECTED') {
        setConnected(true)
        setTimeout(() => {
          onSuccess?.()
          onClose()
        }, 1500)
      }
    } catch (err) {
      console.error('[QrModal] Error verificando estado:', err)
    }
  }, [instanceId, onSuccess, onClose])

  useEffect(() => {
    fetchQR()
    const interval = setInterval(checkStatus, 3000)
    return () => clearInterval(interval)
  }, [fetchQR, checkStatus])

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full relative overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
              <i className="fab fa-whatsapp text-xl text-green-600"></i>
            </div>
            <div>
              <h2 className="text-base font-bold text-gray-900">Escanear QR</h2>
              <p className="text-xs text-gray-500 truncate max-w-[180px]">{instanceName}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={fetchQR}
              disabled={loading}
              title="Recargar QR"
              className="w-9 h-9 flex items-center justify-center rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-600 transition-colors disabled:opacity-50"
            >
              {loading
                ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
                : <i className="fas fa-rotate-right text-sm"></i>
              }
            </button>
            <button onClick={onClose} className="w-9 h-9 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors">
              <i className="fas fa-times"></i>
            </button>
          </div>
        </div>

        {/* Cuerpo */}
        <div className="px-6 py-5">
          {/* Éxito */}
          {connected && (
            <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-xl flex items-center gap-3">
              <i className="fas fa-check-circle text-2xl text-green-600"></i>
              <div>
                <p className="font-semibold text-green-800">¡Conectado exitosamente!</p>
                <p className="text-sm text-green-700">WhatsApp vinculado. Cerrando...</p>
              </div>
            </div>
          )}

          {/* QR */}
          <div className="flex flex-col items-center">
            {loading && !qrData && (
              <div className="w-64 h-64 flex flex-col items-center justify-center bg-gray-50 border-2 border-dashed border-gray-300 rounded-xl">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mb-3"></div>
                <p className="text-sm text-gray-500">Generando QR...</p>
              </div>
            )}

            {qrData && (
              <div className="p-2 bg-white border-2 border-gray-100 rounded-xl shadow-inner">
                <img
                  src={qrData}
                  alt="Código QR de WhatsApp"
                  className="w-64 h-64 object-contain rounded-md"
                />
              </div>
            )}

            {error && !qrData && (
              <div className="w-64 p-4 bg-amber-50 border border-amber-200 rounded-xl text-center">
                <i className="fas fa-exclamation-triangle text-amber-500 text-2xl mb-2"></i>
                <p className="text-sm text-amber-800">{error}</p>
                <button
                  onClick={fetchQR}
                  className="mt-3 inline-flex items-center gap-1 px-3 py-1.5 bg-amber-500 text-white rounded-lg text-xs font-medium hover:bg-amber-600 transition-colors"
                >
                  <i className="fas fa-rotate-right"></i> Reintentar
                </button>
              </div>
            )}
          </div>

          {/* Instrucciones */}
          {!connected && (
            <div className="mt-4 p-3 bg-gray-50 rounded-xl border border-gray-200">
              <p className="text-xs text-gray-600 text-center">
                <i className="fas fa-mobile-alt mr-1.5 text-green-500"></i>
                Abre WhatsApp → Configuración → Dispositivos vinculados → Vincular dispositivo
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 pb-5 flex gap-3">
          <button
            onClick={fetchQR}
            disabled={loading}
            className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium transition-colors disabled:opacity-50"
          >
            {loading ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div> : <i className="fas fa-rotate-right"></i>}
            Recargar QR
          </button>
          <button
            onClick={onClose}
            className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm transition-colors"
          >
            <i className="fas fa-times"></i>
            Cerrar
          </button>
        </div>
      </div>
    </div>
  )
}
